import React from 'react';
import styled from 'styled-components';
import { injectGlobal } from 'react-emotion';

/* global tw */

/*
 * "How it works" — "The Driven Route".
 * A glowing headlight orb drives a winding SVG route down the 5 steps.
 * The gradient trail lights up behind it (stroke-dashoffset), and each
 * node flares + its text reveals the moment the orb passes (staggered
 * animation-delays). Plays once when the section scrolls into view.
 *
 * Coordinate note: the orb travels via CSS offset-path using the SAME
 * path string as the SVG, in a fixed 56x700 px space, so the orb stays
 * exactly on the drawn road. Each step is a fixed 140px tall, so the 5
 * nodes line up with the route nodes at y = 70/210/350/490/630.
 */

// Winding road through nodes at x=28, y = 70,210,350,490,630 (56x700 box)
const ROUTE = "M 28 0 L 28 70 C 46 110 46 170 28 210 C 10 250 10 310 28 350 C 46 390 46 450 28 490 C 10 530 10 590 28 630 L 28 700";

const DRIVE_DURATION = '4.2s';

const STEPS = [
    { num: '01', title: 'Create Your Event / Booking', desc: 'Set travel requirements in minutes.', at: 0.40 },
    { num: '02', title: 'Guests Book or Get Assigned', desc: 'Via private link or QR-based system.', at: 1.25 },
    { num: '03', title: 'We Assign & Manage Vehicles', desc: 'Smart allocation and chauffeur coordination.', at: 2.10 },
    { num: '04', title: 'Track Everything in Real-Time', desc: 'Live updates via dashboard, WhatsApp & SMS.', at: 2.95 },
    { num: '05', title: 'Flawless Execution on Ground', desc: 'Every pickup, every drop — handled.', at: 3.80 },
];

injectGlobal`
    @keyframes zhiwDrive {
        0%   { offset-distance: 0%;   opacity: 0; }
        5%   { opacity: 1; }
        95%  { opacity: 1; }
        100% { offset-distance: 100%; opacity: 0; }
    }
    @keyframes zhiwTrail {
        from { stroke-dashoffset: 100; }
        to   { stroke-dashoffset: 0; }
    }
    @keyframes zhiwFlare {
        0%   { opacity: 0.3; transform: translate(-50%, -50%) scale(0.6);
               box-shadow: 0 0 0 0 rgba(177,92,255,0); border-color: rgba(255,255,255,0.15); }
        45%  { opacity: 1;   transform: translate(-50%, -50%) scale(1.32);
               box-shadow: 0 0 26px 8px rgba(177,92,255,0.6); border-color: rgba(201,162,255,0.95); }
        100% { opacity: 1;   transform: translate(-50%, -50%) scale(1);
               box-shadow: 0 0 16px 3px rgba(177,92,255,0.35); border-color: rgba(201,162,255,0.7); }
    }
    @keyframes zhiwReveal {
        from { opacity: 0; transform: translateY(16px); }
        to   { opacity: 1; transform: translateY(0); }
    }
`

const Section = styled.section`
    ${tw`w-screen flex flex-wrap items-center justify-center relative`};
    background: #0A0A0F;
    background-image:
        radial-gradient(ellipse 40% 30% at 10% 20%, rgba(170,11,202,0.06), transparent 70%),
        radial-gradient(ellipse 30% 25% at 90% 80%, rgba(201,162,255,0.05), transparent 70%);
    padding-top: 3rem;
    padding-bottom: 3rem;
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
    @media (min-width: 992px){
        padding-top: 5rem;
        padding-bottom: 5rem;
    }
`

const Grid = styled.div`
    ${tw`w-full flex flex-wrap max-w-row mx-auto px-6 lg:px-10 justify-between items-start`};
    margin-top: 3rem;
    @media (min-width: 992px){ margin-top: 4.5rem; }
`

const Intro = styled.div`
    ${tw`w-full`};
    margin-bottom: 2.5rem;
    @media (min-width: 992px) { margin-bottom: 3rem; }
    p.eyebrow {
        font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.14em;
        font-size: 0.6875rem;
        color: #C9A2FF;
        margin-bottom: 0.875rem;
    }
    h3 {
        font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
        font-weight: 800;
        font-size: 2rem;
        color: #F5F5F7;
        letter-spacing: -0.035em;
        line-height: 1.1;
        margin: 0;
        @media (min-width: 992px) { font-size: 2.75rem; }
    }
    .rule {
        display: block;
        margin-top: 1rem;
        height: 2px;
        width: 2.5rem;
        background: #C9A2FF;
        border-radius: 2px;
    }
`

const RouteWrap = styled.div`
    ${tw`relative w-full`};

    .route {
        position: absolute;
        top: 0;
        left: 0;
        width: 56px;
        height: 700px;
        z-index: 1;
        pointer-events: none;
    }
    .route .trail {
        stroke-dasharray: 100;
        stroke-dashoffset: 0; /* SSR / no-JS: fully lit */
    }

    .car {
        position: absolute;
        top: 0;
        left: 0;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        z-index: 3;
        opacity: 0; /* hidden until it drives */
        offset-path: path("${ROUTE}");
        offset-distance: 0%;
        offset-rotate: 0deg;
        background: radial-gradient(circle at 50% 50%, #FFFFFF 0%, #E9D5FF 28%, #B15CFF 62%, rgba(177,92,255,0) 100%);
        box-shadow:
            0 0 18px 6px rgba(177,92,255,0.7),
            0 0 42px 14px rgba(177,92,255,0.35);
        pointer-events: none;
    }

    .step {
        position: relative;
        height: 140px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding-left: 5rem;
        @media (min-width: 992px) { padding-left: 6rem; }
    }

    .node {
        position: absolute;
        left: 28px;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 2.6rem;
        height: 2.6rem;
        border-radius: 50%;
        z-index: 2;
        display: flex;
        align-items: center;
        justify-content: center;
        background: radial-gradient(circle at 30% 30%, #1A1022 0%, #0A0A0F 75%);
        border: 1px solid rgba(201,162,255,0.7);
        box-shadow: 0 0 16px 3px rgba(177,92,255,0.35); /* SSR / no-JS: lit */
        .num {
            font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
            font-weight: 800;
            font-size: 0.95rem;
            letter-spacing: -0.02em;
            color: #F5F5F7;
        }
    }

    .content {
        @media (min-width: 992px) {
            display: flex;
            align-items: baseline;
            gap: 3rem;
        }
        h4 {
            font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
            font-weight: 700;
            font-size: 1.1875rem;
            color: #F5F5F7;
            letter-spacing: -0.025em;
            line-height: 1.25;
            margin: 0 0 0.4rem 0;
            @media (min-width: 992px) { font-size: 1.375rem; flex: 0 0 18rem; margin-bottom: 0; }
        }
        p {
            font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
            font-weight: 400;
            font-size: 0.9875rem;
            color: #9898A8;
            line-height: 1.6;
            letter-spacing: -0.005em;
            margin: 0;
            max-width: 34rem;
            @media (min-width: 992px) { font-size: 1.0625rem; flex: 1; }
        }
    }

    /* ---- armed (JS mounted, before/while playing): hide what will animate ---- */
    &.armed .trail { stroke-dashoffset: 100; }
    &.armed .car   { offset-distance: 0%; opacity: 0; }
    &.armed .node {
        opacity: 0.3;
        transform: translate(-50%, -50%) scale(0.6);
        box-shadow: 0 0 0 0 rgba(177,92,255,0);
        border-color: rgba(255,255,255,0.15);
    }
    &.armed .content { opacity: 0; transform: translateY(16px); }

    /* ---- driving: run the cinematic ---- */
    &.armed.driving .car   { animation: zhiwDrive ${DRIVE_DURATION} linear forwards; }
    &.armed.driving .trail { animation: zhiwTrail ${DRIVE_DURATION} linear forwards; }
    &.armed.driving .node    { animation: zhiwFlare 0.9s cubic-bezier(.2,.8,.2,1) both; }
    &.armed.driving .content { animation: zhiwReveal 0.7s ease-out both; }

    @media (prefers-reduced-motion: reduce) {
        &.armed .car { display: none; }
        &.armed .trail { stroke-dashoffset: 0 !important; animation: none !important; }
        &.armed .node {
            opacity: 1 !important;
            transform: translate(-50%, -50%) !important;
            animation: none !important;
            box-shadow: 0 0 16px 3px rgba(177,92,255,0.35) !important;
            border-color: rgba(201,162,255,0.7) !important;
        }
        &.armed .content { opacity: 1 !important; transform: none !important; animation: none !important; }
    }
`

class SectionThree extends React.Component {
    constructor(props) {
        super(props);
        this.state = { armed: false, driving: false };
        this.wrapRef = React.createRef();
    }

    componentDidMount() {
        // Arm first (sets the pre-animation hidden state), then observe.
        this.setState({ armed: true });
        const node = this.wrapRef.current;
        if (!node || typeof IntersectionObserver === 'undefined') {
            // No IO support: just play.
            this.setState({ driving: true });
            return;
        }
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
                    this.setState({ driving: true });
                    if (this.observer) { this.observer.disconnect(); }
                }
            });
        }, { threshold: [0, 0.3, 0.6] });
        this.observer.observe(node);
    }

    componentWillUnmount() {
        if (this.observer) { this.observer.disconnect(); }
    }

    render() {
        const cls = `${this.state.armed ? 'armed' : ''} ${this.state.driving ? 'driving' : ''}`.trim();
        return (
            <Section>
                <Grid>
                    <Intro>
                        <p className="eyebrow">How it works</p>
                        <h3>Seamless From Start to Finish</h3>
                        <span className="rule" />
                    </Intro>

                    <RouteWrap ref={this.wrapRef} className={cls}>
                        <svg className="route" viewBox="0 0 56 700" width="56" height="700" aria-hidden="true">
                            <defs>
                                <linearGradient id="zhiwGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#E9D5FF" />
                                    <stop offset="55%" stopColor="#B15CFF" />
                                    <stop offset="100%" stopColor="#8B3DF5" />
                                </linearGradient>
                                <filter id="zhiwGlow" x="-60%" y="-60%" width="220%" height="220%">
                                    <feGaussianBlur stdDeviation="2.4" />
                                </filter>
                            </defs>
                            <path className="base" d={ROUTE} fill="none"
                                stroke="rgba(201,162,255,0.16)" strokeWidth="2" strokeLinecap="round" />
                            <path className="trail" d={ROUTE} fill="none"
                                stroke="url(#zhiwGrad)" strokeWidth="2.5" strokeLinecap="round"
                                pathLength="100" filter="url(#zhiwGlow)" />
                        </svg>

                        <div className="car" aria-hidden="true" />

                        {STEPS.map((step) => (
                            <div className="step" key={step.num}>
                                <div className="node" style={{ animationDelay: `${step.at}s` }}>
                                    <span className="num">{step.num}</span>
                                </div>
                                <div className="content" style={{ animationDelay: `${step.at + 0.15}s` }}>
                                    <h4>{step.title}</h4>
                                    <p>{step.desc}</p>
                                </div>
                            </div>
                        ))}
                    </RouteWrap>
                </Grid>
            </Section>
        );
    }
}

export default SectionThree;
